import React, { useRef, useState } from 'react'
import {
  createSamlTenant,
  METADATA_ALLOWED_MIME_TYPES,
  METADATA_MAX_FILE_SIZE,
  SamlTenantUploadResult,
} from '../../modules/SamlTenant'
import { getErrorString } from '../../utils/errorUtils'

type MetadataSource = 'file' | 'xml'

type Props = {
  defaultTeamDomain: string
}

const MAX_FILE_SIZE_LABEL = `${METADATA_MAX_FILE_SIZE / (1024 * 1024)}MB`

const isXmlFile = (file: File): boolean => (
  METADATA_ALLOWED_MIME_TYPES.includes(file.type)
  // ブラウザや環境によってはXMLのMIMEタイプが空になるため拡張子でも判定する
  || (file.type === '' && file.name.toLowerCase().endsWith('.xml'))
)

const validateFile = (file: File | null): string | null => {
  if (!file) return 'メタデータファイルを選択してください。'
  if (!isXmlFile(file)) {
    return 'XMLファイル（application/xml もしくは text/xml）を選択してください。'
  }
  if (file.size > METADATA_MAX_FILE_SIZE) {
    return `ファイルサイズが大きすぎます。${MAX_FILE_SIZE_LABEL}以下のファイルを選択してください。`
  }
  return null
}

const validateXml = (xml: string): string | null => {
  const trimmed = xml.trim()
  if (trimmed.length === 0) return 'メタデータXMLを入力してください。'
  if (!trimmed.startsWith('<')) {
    return 'メタデータXMLの形式が不正です。XML全文を貼り付けてください。'
  }
  if (trimmed.length > METADATA_MAX_FILE_SIZE) {
    return `XMLが大きすぎます。${MAX_FILE_SIZE_LABEL}以下に収めてください。`
  }
  return null
}

const SamlMetadataUploadForm = ({ defaultTeamDomain }: Props) => {
  const [teamDomain, setTeamDomain] = useState(defaultTeamDomain)
  const [metadataSource, setMetadataSource] = useState<MetadataSource>('file')
  const [metadataFile, setMetadataFile] = useState<File | null>(null)
  const [metadataXml, setMetadataXml] = useState('')
  const [metadataUrl, setMetadataUrl] = useState('')
  const [enabled, setEnabled] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<SamlTenantUploadResult | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const validate = (): string | null => {
    if (teamDomain.trim().length === 0) return 'チームドメインを入力してください。'
    return metadataSource === 'file' ? validateFile(metadataFile) : validateXml(metadataXml)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null
    setMetadataFile(file)
    setError(file ? validateFile(file) : null)
  }

  const handleSourceChange = (source: MetadataSource) => {
    setMetadataSource(source)
    setError(null)
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const validationError = validate()
    if (validationError) {
      setError(validationError)
      setResult(null)
      return
    }

    setIsSubmitting(true)
    setError(null)
    try {
      const uploaded = await createSamlTenant({
        teamDomain: teamDomain.trim(),
        metadataFile: metadataSource === 'file' ? metadataFile : null,
        metadataXml: metadataSource === 'xml' ? metadataXml.trim() : null,
        metadataUrl: metadataUrl.trim() || null,
        enabled,
      })
      setResult(uploaded)
      // raw XMLはフロント側に保持しない
      setMetadataXml('')
      setMetadataFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (submitError) {
      setResult(null)
      setError(getErrorString(submitError))
    } finally {
      setIsSubmitting(false)
    }
  }

  const samlTenant = result?.saml_tenant

  return (
    <form className="saml-form" onSubmit={handleSubmit} noValidate>
      <div className="saml-field">
        <label htmlFor="saml-team-domain">チームドメイン*</label>
        <input
          id="saml-team-domain"
          type="text"
          value={teamDomain}
          onChange={(event) => setTeamDomain(event.target.value)}
        />
        <p className="saml-help-text">SAMLを設定するTeamのdomain（例: example）を入力します。</p>
      </div>

      <fieldset className="saml-field saml-field--source">
        <legend>メタデータの指定方法*</legend>
        <label className="saml-choice" htmlFor="saml-source-file">
          <input
            id="saml-source-file"
            type="radio"
            name="metadata-source"
            checked={metadataSource === 'file'}
            onChange={() => handleSourceChange('file')}
          />
          <span>XMLファイルをアップロード</span>
        </label>
        <label className="saml-choice" htmlFor="saml-source-xml">
          <input
            id="saml-source-xml"
            type="radio"
            name="metadata-source"
            checked={metadataSource === 'xml'}
            onChange={() => handleSourceChange('xml')}
          />
          <span>XMLを貼り付け</span>
        </label>
      </fieldset>

      {metadataSource === 'file' ? (
        <div className="saml-field">
          <label htmlFor="saml-metadata-file">フェデレーションメタデータXML*</label>
          <div className="saml-file-input">
            <input
              id="saml-metadata-file"
              ref={fileInputRef}
              type="file"
              accept=".xml,application/xml,text/xml"
              onChange={handleFileChange}
            />
          </div>
          <p className="saml-help-text">
            {`Entraの「フェデレーション メタデータ XML」を選択します（${MAX_FILE_SIZE_LABEL}以下）。`}
          </p>
        </div>
      ) : (
        <div className="saml-field">
          <label htmlFor="saml-metadata-xml">フェデレーションメタデータXML*</label>
          <textarea
            id="saml-metadata-xml"
            rows={10}
            value={metadataXml}
            onChange={(event) => setMetadataXml(event.target.value)}
          />
          <p className="saml-help-text">XML全文を貼り付けます。送信後は画面上に保持しません。</p>
        </div>
      )}

      <div className="saml-field">
        <label htmlFor="saml-metadata-url">メタデータURL</label>
        <input
          id="saml-metadata-url"
          type="text"
          value={metadataUrl}
          onChange={(event) => setMetadataUrl(event.target.value)}
        />
        <p className="saml-help-text">任意。参照元URLを記録として保存します。</p>
      </div>

      <div className="saml-field saml-field--checkbox">
        <label className="saml-choice" htmlFor="saml-enabled">
          <input
            id="saml-enabled"
            type="checkbox"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          <span>SAMLログインを有効にする</span>
        </label>
      </div>

      {error && (
        <div className="saml-message saml-message--error" role="alert">{error}</div>
      )}

      <button type="submit" className="btn btn-primary saml-submit" disabled={isSubmitting}>
        {isSubmitting ? '送信中...' : 'アップロードする'}
      </button>

      {samlTenant && (
        <div className="saml-result" data-testid="saml-result">
          <h3>SAML設定を保存しました</h3>
          <dl>
            <dt>チームドメイン</dt>
            <dd>{result?.team_domain}</dd>
            <dt>エンティティID</dt>
            <dd>{samlTenant.entity_id}</dd>
            <dt>SSO URL</dt>
            <dd>{samlTenant.sso_url || '-'}</dd>
            <dt>SLO URL</dt>
            <dd>{samlTenant.slo_url || '-'}</dd>
            <dt>SAMLログイン</dt>
            <dd>{samlTenant.enabled ? '有効' : '無効'}</dd>
          </dl>
          <details className="saml-result__internal">
            <summary>管理者向け情報（テナントID・証明書）</summary>
            <dl>
              <dt>テナントID</dt>
              <dd>{samlTenant.tenant_id}</dd>
              <dt>証明書フィンガープリント</dt>
              <dd>{samlTenant.certificate_fingerprint || '-'}</dd>
            </dl>
          </details>
        </div>
      )}
    </form>
  )
}

export default SamlMetadataUploadForm
